import PropTypes from 'prop-types';
import { useLayoutEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
  addPageData,
  navigateWizard,
  persistUnsupportedSchoolsInterest,
  sendBuilderState,
} from '../../actions';
import LoadingButton from '../../components/LoadingButton';
import PageLayout from '../../components/PageLayout/PageLayout';
import SelectLoadingFetch from '../../components/SelectLoadingFetch';
import analytics from '../../lib/analytics';
import { fullDateFormat } from '../../lib/dateFormatter';
import { track } from '../../lib/heapHelpers';
import useCampuses from '../../lib/hooks/useCampuses';
import useCourses from '../../lib/hooks/useCourses';
import usePeriods from '../../lib/hooks/usePeriods';
import useSchools from '../../lib/hooks/useSchools';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

export const WhereToStudyPage = ({
  dispatchAddPageData,
  dispatchWizardNav,
  dispatchUpdateProgress,
  dispatchUpdateUnsupportedSchoolsInterest,
}) => {
  const { selected: selectedSchool } = useSchools();
  const { allCampuses: campuses, selectedCampus } = useCampuses();
  const { selectedCourse } = useCourses();
  const { periods, data: periodOptions, selectedPeriod: currentPeriod } = usePeriods();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchCourseTerm, setSearchCourseTerm] = useState('');
  const { periodGuid: currentPeriodValue = '' } = currentPeriod || {};
  const selectedPeriodOption = periodOptions.find(p => p.value === currentPeriodValue) ?? null;

  const isInvalid =
    !selectedSchool ||
    !selectedCourse ||
    !selectedCampus ||
    Object.keys(selectedCourse ?? {}).length === 0 ||
    !periodOptions ||
    (periodOptions.length > 0 && !selectedPeriodOption) ||
    (!periodOptions.length && selectedCourse.validated);

  useLayoutEffect(() => {
    track('View - School page');
  }, []);

  const handleSubmit = event => {
    event.preventDefault();

    analytics.sendEvent('Click - Next', 'where to study');

    const hasSingleCampus = selectedSchool.campuses.length === 1;
    const isNotSupported = selectedSchool.supported === false;

    if (hasSingleCampus && !isNotSupported) {
      const { value: schoolId } = selectedSchool;
      const [campus] = campuses[schoolId];
      const campusPayload = payloadFactory.createAddPageDataPayload(
        'campus',
        campus,
        'campusGuid',
        selectedSchool.campuses[0],
      );
      dispatchAddPageData(campusPayload);
    }

    if (isNotSupported) {
      dispatchUpdateUnsupportedSchoolsInterest();
    }

    track('Submit - Where To Study page', {
      school_name: selectedSchool.label,
      course_name: selectedCourse.label,
      search_keyword: [searchTerm, searchCourseTerm],
    });

    dispatchWizardNav({ dir: 'fwd' });
  };

  const setSchool = school => {
    const value = school?.value;
    const label = school?.label;
    const hasChanged = value !== selectedSchool?.value;

    if (!school) {
      setCourse(null);
    }

    const schoolPayload = payloadFactory.createAddPageDataPayload(
      'school',
      school,
      'schoolGuid',
      value,
    );
    const schoolNamePayload = payloadFactory.createAddPageDataPayload('schoolName', label);

    dispatchAddPageData(schoolPayload);
    dispatchAddPageData(schoolNamePayload);

    if (hasChanged) {
      const clearCampusPayload = payloadFactory.createResetPageDataPayload('campus', 'campusGuid');
      dispatchAddPageData(clearCampusPayload);
      setCampus(null);
      setCourse(null);
      setPeriod(null);
    }

    setSearchTerm(label);
  };

  const setCampus = campus => {
    const campusGuid = campus?.value;
    const payload = payloadFactory.createAddPageDataPayload(
      'campus',
      campus,
      'campusGuid',
      campusGuid,
    );
    dispatchAddPageData(payload);
  };

  const setCourse = course => {
    let attendance = course?.attendance;
    let courseGuid = course?.value;
    let seasonClassGuid = course?.seasonClassGuid;
    let underReview = course?.underReview;
    let label = course?.label;

    const hasChanged = course?.value !== selectedCourse?.value;

    if (!course) {
      setPeriod(null);
    }

    setSearchCourseTerm(label);

    const fullTimeCourse = attendance === 'FullTime';
    const isUnderReview = underReview === true;

    const coursePayload = payloadFactory.createAddPageDataPayload('course', course);
    dispatchAddPageData(coursePayload);
    const courseGuidPayload = payloadFactory.createAddPageDataPayload('courseGuid', courseGuid);
    dispatchAddPageData(courseGuidPayload);

    const courseNamePayload = payloadFactory.createAddPageDataPayload('courseName', label);

    dispatchAddPageData(courseNamePayload);
    dispatchAddPageData(payloadFactory.createAddPageDataPayload('periodStartDate', null));

    const partTimeCoursePayload = payloadFactory.createAddPageDataPayload(
      'partTimeCourse',
      !fullTimeCourse,
    );
    dispatchAddPageData(partTimeCoursePayload);

    const seasonClassGuidPayload = payloadFactory.createAddPageDataPayload(
      'classId',
      seasonClassGuid,
    );
    dispatchAddPageData(seasonClassGuidPayload);

    if (isUnderReview) {
      const stateOfApplicationPayload = payloadFactory.createAddPageDataPayload(
        'stateOfApplication',
        'under_review',
      );
      dispatchAddPageData(stateOfApplicationPayload);
      dispatchUpdateUnsupportedSchoolsInterest();
      dispatchUpdateProgress();
    }

    if (hasChanged) {
      const clearPeriodsPayload = payloadFactory.createResetPageObjectPayLoad('periods', null);
      dispatchAddPageData(clearPeriodsPayload);
      const clearPeriodTimeStampPayload = payloadFactory.createResetPageDataPayload(
        'periodsTimeStamp',
        null,
      );
      dispatchAddPageData(clearPeriodTimeStampPayload);

      const clearCurrentLoanPeriodPayload = payloadFactory.createAddPageDataPayload(
        'currentLoanPeriod',
        {
          personalSavings: '',
          relativesContribution: '',
          amount: '',
          currency: '',
        },
        'currentLoanPeriod',
        null,
      );
      dispatchAddPageData(clearCurrentLoanPeriodPayload);

      const clearRemainderLoanPeriodPayload = payloadFactory.createAddPageDataPayload(
        'remainderLoanPeriod',
        {
          personalSavings: '',
          relativesContribution: '',
          amount: '',
          currency: '',
        },
        'remainderLoanPeriod',
        null,
      );

      dispatchAddPageData(clearRemainderLoanPeriodPayload);
    }
  };

  const setPeriod = newPeriod => {
    let periodGuid, currency, minLoanSize, startDate, period;
    if (newPeriod) {
      period = periods.find(p => {
        const { periodGuid } = p;
        const { value } = newPeriod;

        return periodGuid === value;
      });

      periodGuid = period.periodGuid;
      currency = period.currency;
      minLoanSize = period.minLoanSize;
      startDate = period.startDate;
    }

    dispatchAddPageData(
      payloadFactory.createAddPageDataPayload('period', period, 'coursePeriodGuid', periodGuid),
    );

    dispatchAddPageData(payloadFactory.createAddPageDataPayload('baseCurrency', currency));
    dispatchAddPageData(
      payloadFactory.createAddPageDataPayload(
        'periodStartDate',
        startDate ? fullDateFormat(startDate) : null,
      ),
    );

    dispatchAddPageData(payloadFactory.createAddPageDataPayload('courseMinLoanSize', minLoanSize));
  };

  return (
    <PageLayout
      title="Where, what and when do you plan to study?"
      helpText="You do not need to have an admission letter at this stage."
    >
      <form onSubmit={handleSubmit}>
        <div>
          <SelectLoadingFetch
            key="school"
            single="school"
            plural="schools"
            onChange={setSchool}
            selected={selectedSchool}
            hook={useSchools}
            fuseConfigKeys={['label', 'acronym', 'schoolName', 'universityName']}
            minCharSearchLength={3}
            label="School"
          />
        </div>
        <div className="mt-4">
          <SelectLoadingFetch
            key="campus"
            single="campus"
            plural="campuses"
            onChange={setCampus}
            selected={selectedCampus}
            hook={useCampuses}
            label="Campus"
          />
        </div>
        <div className="mt-4">
          <SelectLoadingFetch
            key="course"
            single="course"
            plural="courses"
            onChange={setCourse}
            selected={selectedCourse}
            hook={useCourses}
            label="Course"
          />
        </div>
        <div className="mt-4">
          <SelectLoadingFetch
            key="class"
            single="class"
            plural="classses"
            onChange={setPeriod}
            selected={selectedPeriodOption}
            hook={usePeriods}
            label="Class"
            noOptionsMessage="No classes found"
            displayError={true}
          />
        </div>

        <div className="mt-12">
          <LoadingButton type="submit" className="niso-button" isDisabled={isInvalid}>
            Next
          </LoadingButton>
        </div>
      </form>
    </PageLayout>
  );
};

WhereToStudyPage.defaultProps = {
  currentPeriod: null,
  periods: [],
};

WhereToStudyPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchUpdateUnsupportedSchoolsInterest: PropTypes.func.isRequired,
  dispatchUpdateProgress: PropTypes.func.isRequired,
};

export const mapStateToProps = state => ({});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
  dispatchUpdateUnsupportedSchoolsInterest: persistUnsupportedSchoolsInterest,
  dispatchUpdateProgress: sendBuilderState,
};

export default connect(mapStateToProps, mapDispatchToProps)(WhereToStudyPage);
